import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { SurveyConfigurationApiService } from '../../../apis/admin/survey-configuration-api.service';
import {
  SurveyConfigurationCreate,
  SurveyConfigurationResponse,
  SurveyConfigurationUpdate,
  SurveyLinkResponse,
  SurveyParticipationPopupStatusResponse,
  SurveyParticipationResponse,
} from '../../../models/admin/survey-configuration.model';

@Injectable({
  providedIn: 'root'
})
export class SurveyConfigurationService {

  private _configurations = new BehaviorSubject<SurveyConfigurationResponse[]>([]);
  configurations$ = this._configurations.asObservable();

  constructor(private surveyConfigurationApiService: SurveyConfigurationApiService) { }

  get configurations(): SurveyConfigurationResponse[] {
    return this._configurations.value;
  }

  loadConfigurations(): Observable<SurveyConfigurationResponse[]> {
    return this.surveyConfigurationApiService.getSurveyConfigurationList().pipe(
      tap(configs => this._configurations.next(configs))
    );
  }

  createConfigurations(rows: SurveyConfigurationCreate[]): Observable<SurveyConfigurationResponse[]> {
    return this.surveyConfigurationApiService.createSurveyConfigurations({ rows }).pipe(
      tap(created => this._configurations.next([...this._configurations.value, ...created]))
    );
  }

  updateConfigurations(rows: SurveyConfigurationUpdate[]): Observable<SurveyConfigurationResponse[]> {
    return this.surveyConfigurationApiService.updateSurveyConfigurations({ rows }).pipe(
      tap(updated => {
        const updatedMap = new Map(updated.map(r => [r.id, r]));
        const merged = this._configurations.value.map(c => updatedMap.get(c.id) ?? c);
        this._configurations.next(merged);
      })
    );
  }

  deleteConfiguration(survey_id: number, updated_by: number): Observable<SurveyConfigurationResponse> {
    return this.surveyConfigurationApiService.deleteSurveyConfiguration(survey_id, updated_by).pipe(
      tap(() => {
        const filtered = this._configurations.value.filter(c => c.id !== survey_id);
        this._configurations.next(filtered);
      })
    );
  }

  getSurveyLink(business_unit_code: string, region_code: string): Observable<SurveyLinkResponse> {
    return this.surveyConfigurationApiService.getSurveyLink(business_unit_code, region_code);
  }

  getPopupStatus(wwid: number, business_unit_code: string, region_code: string): Observable<SurveyParticipationPopupStatusResponse> {
    return this.surveyConfigurationApiService.getSurveyParticipationPopupStatus(wwid, business_unit_code, region_code);
  }

  markParticipated(wwid: number, survey_id: number): Observable<SurveyParticipationResponse> {
    return this.surveyConfigurationApiService.markParticipated(wwid, survey_id);
  }

  remindLater(wwid: number, survey_id: number): Observable<SurveyParticipationResponse> {
    return this.surveyConfigurationApiService.remindLater(wwid, survey_id);
  }

  remindTomorrow(wwid: number, survey_id: number): Observable<SurveyParticipationResponse> {
    return this.surveyConfigurationApiService.remindTomorrow(wwid, survey_id);
  }

  optOut(wwid: number, survey_id: number): Observable<SurveyParticipationResponse> {
    return this.surveyConfigurationApiService.optOut(wwid, survey_id);
  }

  /**
   * Validates that no two rows share the same BU + region combination.
   * @param rows - Array of objects with `business_unit_code` and `region_code`
   * @returns `null` when valid, or an object `{ duplicateIndices: number[] }` listing
   *          the 0-based indices of every row involved in a duplicate pair
   */
  validateNoDuplicateBuRegion(
    rows: { business_unit_code: string; region_code: string }[]
  ): { duplicateIndices: number[] } | null {
    const seen = new Map<string, number>();
    const duplicateIndices = new Set<number>();
    rows.forEach((row, idx) => {
      if (!row.business_unit_code || !row.region_code) return;
      const key = `${row.business_unit_code}|${row.region_code}`;
      if (seen.has(key)) {
        duplicateIndices.add(seen.get(key)!);
        duplicateIndices.add(idx);
      } else {
        seen.set(key, idx);
      }
    });
    return duplicateIndices.size > 0 ? { duplicateIndices: [...duplicateIndices] } : null;
  }

  /**
   * Validates that survey start date+time is strictly before the end date+time.
   * @param startDate  - Date object for the open date
   * @param startTime  - Time string in "HH:MM" format for the open time
   * @param endDate    - Date object for the close date
   * @param endTime    - Time string in "HH:MM" format for the close time
   * @returns `true` when the range is valid, `false` otherwise
   */
  validateSurveyDateRange(
    startDate: Date | null,
    startTime: string,
    endDate: Date | null,
    endTime: string
  ): boolean {
    if (!startDate || !startTime || !endDate || !endTime) return true; // defer to required validators
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), startHour, startMin);
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), endHour, endMin);
    return start < end;
  }
}
